import { useCallback, useEffect, useRef, useState } from 'react';

const DEFAULT_SCROLL_STEP = 200;

export const computeOverflow = ({ scrollLeft, scrollWidth, clientWidth }) => ({
  canScrollLeft: scrollLeft > 0,
  canScrollRight: scrollLeft + clientWidth < scrollWidth - 1,
});

const useHorizontalOverflow = (deps = [], scrollStep = DEFAULT_SCROLL_STEP) => {
  const ref = useRef(null);
  const [overflow, setOverflow] = useState({
    canScrollLeft: false,
    canScrollRight: false,
  });

  const update = useCallback(() => {
    const el = ref.current;
    if (!el) return;

    const { scrollLeft, scrollWidth, clientWidth } = el;
    setOverflow(computeOverflow({ scrollLeft, scrollWidth, clientWidth }));
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    update();
    el.addEventListener('scroll', update, { passive: true });

    const resizeObserver = new ResizeObserver(update);
    resizeObserver.observe(el);

    return () => {
      el.removeEventListener('scroll', update);
      resizeObserver.disconnect();
    };
  }, [update, ...deps]);

  const scrollByStep = useCallback(
    (direction) => {
      const el = ref.current;
      if (!el) return;

      el.scrollBy({ left: direction * scrollStep, behavior: 'smooth' });
    },
    [scrollStep],
  );

  return {
    ref,
    canScrollLeft: overflow.canScrollLeft,
    canScrollRight: overflow.canScrollRight,
    scrollByStep,
  };
};

export default useHorizontalOverflow;
